/**
 * GRIPS-INTERACTION PR-2 (Ctrl→Enter/Space correction, §8.2) — hot-grip
 * MOVE / ROTATE / SCALE / MIRROR command builder.
 *
 * `hotGripStore.cycleArmedEditMode` (Enter/Space while a grip is armed) cycles
 * the modal grip prompt through Stretch → Move → Rotate → Scale → Mirror →
 * (wraps). STRETCH keeps reshaping through the existing pseudo-property path
 * (`gripDragTranslate.ts` → `properties/apply`, unchanged by this module). The
 * other four modes are AutoCAD's classic RIGID transforms: this pure module
 * maps the armed grip's base point + the committed cursor point into the
 * SAME `EditorCommand` union members the click-based MOVE / ROTATE / SCALE /
 * MIRROR draft commands already dispatch (`draftModifyStateMachine.ts`) — no
 * new command types, no reducer changes.
 *
 * AutoCAD parity, source-verified (see `gripInteractionConstants.ts`'s
 * `GRIP_MODE_LABELS` doc comment for the Autodesk doc citations):
 *
 *   - Base point: "The hot grip becomes the default base point for moving,
 *     rotating, etc." — every mode here uses the ARMED GRIP's own world
 *     position as the base point. Overriding it via the `B` sub-option is
 *     OUT OF SCOPE (`GRIPS-MODE-SUBOPTIONS` follow-up).
 *   - Scope: "Editing multiple grip points" — Move/Rotate/Scale/Mirror act on
 *     the WHOLE CURRENT SELECTION SET, not just the entity that owns the
 *     armed grip (unlike Stretch, which stays single-entity/single-grip).
 *     Callers pass the live `selectedIdsRef.current`, not `[draggedEntityId]`.
 *   - Rotate: absolute angle from base to the target point (0° = +X axis) —
 *     mirrors `draftModifyStateMachine.ts`'s `rotate-angle` step exactly.
 *   - Scale: factor = distance(base, target) — mirrors `scale-factor` exactly,
 *     including its "click on the base point → factor 1" degenerate fallback.
 *   - Mirror: grip MIRROR mode REPLACES the source by default (`deleteSource:
 *     true`) — this is the documented grip-specific default and differs from
 *     the standalone command-line MIRROR command's own default (`deleteSource:
 *     false`, kept in `draftModifyStateMachine.ts`'s interactively-asked
 *     `mirror-second-point` case, unchanged by this module). The Copy
 *     sub-option (hold Ctrl to keep the source) is OUT OF SCOPE
 *     (`GRIPS-MODE-SUBOPTIONS` follow-up).
 */

import {
  FLOAT_EPSILON,
  type CommandSource,
  type EditorCommand,
} from "@dxf-ai-editor/contracts";

/** The four rigid-transform grip modes (Stretch is excluded — separate path). */
export type GripTransformEditMode = "move" | "rotate" | "scale" | "mirror";

/**
 * Build the `EditorCommand` for a grip-driven MOVE / ROTATE / SCALE / MIRROR
 * whole-selection edit.
 *
 * @param mode the cycled edit mode (never "stretch" — the caller only invokes
 *   this once the armed mode has advanced past Stretch).
 * @param entityIds the LIVE selection (`selectedIdsRef.current`), not just the
 *   armed grip's own entity — AutoCAD parity (see module doc comment).
 * @param basePoint the armed grip's own world position, TRUE document/WCS
 *   coords (`HotGripRef.world` / `Grip.world`) — the default base point for
 *   every mode.
 * @param targetPoint the committed cursor point, SAME TRUE-WCS frame as
 *   `basePoint` (the caller maps the raw pointer through `unapplyOffset`
 *   first, exactly like the existing Stretch commit path).
 * @param source the command's `CommandSource` tag.
 * @param mirrtext the LIVE `MIRRTEXT` sysvar value, threaded onto the built
 *   `geometry/mirror-entities` command (ignored by the other three modes).
 *   This module is a pure, store-free function (see the module doc comment)
 *   so it cannot read `useSysvarStore` itself — the caller
 *   (`App.tsx`'s `handleGripTransformCommit`) reads
 *   `useSysvarStore.getState().values.MIRRTEXT` at commit time and passes it
 *   in, mirroring how `useDraftSession.ts`'s `dispatchWithMleaderEditGuards`
 *   stamps the same live sysvar onto the click-based MIRROR command at its
 *   own dispatch chokepoint ("AutoCAD captures the sysvar when the command
 *   executes"). Defaults to `0` (AutoCAD's own default, text stays readable)
 *   so every existing call site that does not pass it keeps its current,
 *   already-tested behaviour.
 * @returns the `EditorCommand` to dispatch, or `null` when there is nothing
 *   to commit — either an empty `entityIds` (mirrors `translateGripDrag`'s
 *   null-for-no-op contract) or a degenerate (same-point) MIRROR axis; the
 *   caller skips dispatch in either case. The reducer's own `computeInverse`
 *   already no-ops an exact-identity move(0,0)/rotate(0°)/scale(1) commit, so
 *   no additional epsilon guard is needed here for those three. MIRROR is
 *   different: `editorCommandSchema`'s `geometry/mirror-entities` `.refine()`
 *   (packages/contracts/src/editor.ts) REJECTS (throws, via `.parse()`, not
 *   `.safeParse()`) a mirror axis at/below `FLOAT_EPSILON` — and that parse
 *   runs on the live `editorStore.ts` dispatch path OUTSIDE any try/catch, so
 *   letting it throw here would leave `StageGripDrag.ts`'s `onArmedCommit`
 *   mid-teardown (the batch never closes, listeners never removed — a stuck
 *   armed-grip state machine). Pre-empt it with the SAME threshold, imported
 *   from `editor.ts` so the two can never silently drift apart.
 */
export function buildGripTransformCommand(
  mode: GripTransformEditMode,
  entityIds: readonly string[],
  basePoint: { x: number; y: number },
  targetPoint: { x: number; y: number },
  source: CommandSource = "scene",
  mirrtext: number = 0,
): EditorCommand | null {
  if (entityIds.length === 0) return null;
  const ids = [...entityIds];
  switch (mode) {
    case "move": {
      const delta = {
        x: targetPoint.x - basePoint.x,
        y: targetPoint.y - basePoint.y,
      };
      return {
        type: "geometry/move-entities",
        source,
        entityIds: ids,
        delta,
      };
    }
    case "rotate": {
      const angleDeg =
        (Math.atan2(targetPoint.y - basePoint.y, targetPoint.x - basePoint.x) *
          180) /
        Math.PI;
      return {
        type: "geometry/rotate-entities",
        source,
        entityIds: ids,
        basePoint,
        angleDeg,
      };
    }
    case "scale": {
      const dist = Math.hypot(
        targetPoint.x - basePoint.x,
        targetPoint.y - basePoint.y,
      );
      // Codex-mirrored degenerate fallback (`draftModifyStateMachine.ts`
      // `scale-factor`): clicking on the base point defaults to factor 1
      // instead of a rejected zero/near-zero scale factor.
      const factor = dist < 1e-10 ? 1 : dist;
      return {
        type: "geometry/scale-entities",
        source,
        entityIds: ids,
        basePoint,
        factor,
      };
    }
    case "mirror": {
      // Codex P1 — a degenerate (same-point) mirror axis crashes the whole
      // armed-grip state machine downstream (see the `@returns` doc comment
      // above): `editorCommandSchema.parse()` throws synchronously inside
      // `StageGripDrag.ts`'s `onArmedCommit`, BEFORE `finish("commit")` runs,
      // so the grouped-undo batch never closes and the window listeners never
      // tear down. Guard it here, at the SAME threshold the schema's
      // `.refine()` enforces, and return `null` (the existing "nothing to
      // commit" contract `App.tsx`'s `handleGripTransformCommit` already
      // implements: `if (command) dispatch(command)`) instead of building a
      // command the schema would reject.
      const axisLen = Math.hypot(
        targetPoint.x - basePoint.x,
        targetPoint.y - basePoint.y,
      );
      if (axisLen <= FLOAT_EPSILON) return null;
      return {
        type: "geometry/mirror-entities",
        source,
        entityIds: ids,
        mirrorLine1: basePoint,
        mirrorLine2: targetPoint,
        // Grip MIRROR mode's default REPLACES the source (Autodesk-verified —
        // see module doc comment).
        deleteSource: true,
        // MIRRTEXT — threaded in from the `mirrtext` parameter, which the
        // caller populates from the LIVE `useSysvarStore` value at dispatch
        // time (see the `@param mirrtext` doc comment above). This module
        // stays store-free; it only forwards whatever the caller captured.
        // Defaults to 0 (AutoCAD's own default, text stays readable) when
        // the caller omits it, matching the schema's own `.default(0)`.
        mirrtext,
      };
    }
  }
}
